import React, { useLayoutEffect, useRef } from 'react';
import { Alert, Spin, Tag, Typography } from 'antd';
import { CheckCircleOutlined, LoadingOutlined, RobotOutlined } from '@ant-design/icons';
import Markdown from 'markdown-to-jsx';
import { IMessage, IToolCall } from '../../../services/aiChat/aiChat.service';
import MessageBubble from './MessageBubble';
import InputArea from './InputArea';

const { Title, Paragraph } = Typography;

const EXAMPLE_PROMPTS = [
  'How many active O365 users do we have this month?',
  'Which devices have not checked in for more than 30 days?',
  'Show me the top 10 applications by install count.',
  'What is our total Microsoft 365 license cost per user?',
];

interface ChatWindowProps {
  messages: IMessage[];
  isStreaming: boolean;
  partialText: string;
  toolCalls: IToolCall[];
  socketError: string | null;
  isConnected: boolean;
  messagesLoading: boolean;
  inputValue: string;
  hasActiveConversation: boolean;
  onInputChange: (val: string) => void;
  onSend: () => void;
  onStop: () => void;
  onClearError: () => void;
  onExampleClick: (prompt: string) => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  messages,
  isStreaming,
  partialText,
  toolCalls,
  socketError,
  isConnected,
  messagesLoading,
  inputValue,
  hasActiveConversation,
  onInputChange,
  onSend,
  onStop,
  onClearError,
  onExampleClick,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, partialText, toolCalls]);

  const showWelcome = !hasActiveConversation && messages.length === 0 && !isStreaming;

  return (
    <div className="ai-chat-window">
      {!isConnected && (
        <Alert
          type="warning"
          message="Reconnecting to AI Chat service…"
          banner
          className="ai-chat-reconnecting"
        />
      )}

      <div className="ai-chat-messages">
        {messagesLoading ? (
          <div className="ai-chat-messages-loading">
            <Spin size="large" />
          </div>
        ) : showWelcome ? (
          <div className="ai-chat-welcome">
            <div className="ai-chat-welcome-icon">
              <RobotOutlined />
            </div>
            <Title className="ai-chat-welcome-title">Synapse 360 AI</Title>
            <Paragraph className="ai-chat-welcome-sub">
              Ask questions about your IT assets, licenses, users, and more — powered by your live data.
            </Paragraph>
            <div className="ai-chat-examples">
              {EXAMPLE_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  className="ai-chat-example-btn"
                  onClick={() => onExampleClick(prompt)}
                  type="button"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <MessageBubble key={msg.id} message={msg} />
            ))}

            {isStreaming && (
              <div style={{ display: 'flex', gap: 14, padding: '20px 32px', background: '#f9fafb', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2, fontSize: 18, background: 'linear-gradient(135deg, #1890ff, #096dd9)', color: '#fff', boxShadow: '0 2px 8px rgba(24,144,255,0.32)' }}>
                  <RobotOutlined />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {toolCalls.length > 0 && (
                    <div className="ai-chat-tool-calls">
                      {toolCalls.map((tc, i) => (
                        <div key={i}>
                          {tc.row_count != null ? (
                            <Tag
                              icon={<CheckCircleOutlined />}
                              color="success"
                              className="ai-chat-tool-tag"
                            >
                              {tc.row_count.toLocaleString()} rows returned
                              {tc.description && ` · ${tc.description}`}
                              {tc.capped ? ' · capped at 500' : ''}
                            </Tag>
                          ) : (
                            <Tag
                              icon={<LoadingOutlined />}
                              color="processing"
                              className="ai-chat-tool-tag"
                            >
                              Querying: {tc.description}…
                            </Tag>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {partialText ? (
                    <div className="ai-chat-markdown ai-chat-streaming-text">
                      <Markdown>{partialText}</Markdown>
                    </div>
                  ) : (
                    <div className="ai-chat-thinking-dots">
                      <span />
                      <span />
                      <span />
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {socketError && (
          <Alert
            type="error"
            message={socketError}
            closable
            onClose={onClearError}
            className="ai-chat-error"
          />
        )}

      </div>

      <div className="ai-chat-input-wrapper">
        <InputArea
          value={inputValue}
          onChange={onInputChange}
          onSend={onSend}
          onStop={onStop}
          isStreaming={isStreaming}
          disabled={!isConnected}
        />
        {!isConnected && (
          <Typography.Text type="secondary" className="ai-chat-conn-status">
            Connecting…
          </Typography.Text>
        )}
      </div>
      <div ref={bottomRef} />
    </div>
  );
};

export default ChatWindow;
